import { Flex, Text, useColorModeValue } from '@chakra-ui/react';
import ErrorState from '../../../components/states/ErrorState';
import { translate } from '../../../i18n';
import {
    READ_FAILED_I18N_KEY,
    ReadGate,
    ReadState,
    scopePromptI18nKey,
} from '../../_shared/readState';

/**
 * What an analytics chart renders INSTEAD of a chart when it has no answer.
 *
 * Every chart in this feature draws from `(data || []).map(...)`, so a read
 * that produced no data draws an EMPTY CHART — an axis with no points, a donut
 * whose centre label reads `0`, a heatmap with no series. On the page an
 * operator uses to decide whether the week was quiet, that is not a missing
 * answer; it is a confident wrong one, and it is the chart-shaped spelling of
 * the `0,00 €` run 97 removed from the KPI cards.
 *
 * Two of the four `ReadState`s must never reach a chart:
 *
 * - `unreadable` — the read was issued and refused. `ErrorState` says so, and
 *   carries the retry, matching the four cumulative charts run 99 fixed.
 * - `never-issued` — the read was never sent, because the query's `enabled:`
 *   gate is unsatisfied. This one does NOT resolve on its own: React Query v4
 *   reports a disabled query as `isLoading: true` with `fetchStatus: 'idle'`
 *   forever, so the empty chart is permanent until the operator selects
 *   something, and nothing on screen tells them that is what is missing. The
 *   honest render is a prompt for whatever the gate wants.
 *
 * `loading` and `answered` return `null`, and the caller draws its chart — a
 * transient empty chart under a spinner is an ordinary loading affordance, and
 * a resolved read with no rows is a real answer that the caller's own empty
 * state (if it has one) is entitled to make a claim about.
 *
 * The prompt strings are borrowed keys, not new ones: `src/i18n/{en,fr}.ts` are
 * inside the open-PR fence (R96-7). Both constants are named in
 * `features/_shared/readState.ts` with the un-borrowing instruction.
 */
export default function ChartReadState({
    state,
    gate,
    onRetry,
}: {
    state: ReadState;
    /** Which `enabled:` predicate the read is gated on — it decides the prompt. */
    gate: ReadGate;
    onRetry?: () => void;
}) {
    const promptColor = useColorModeValue('secondaryGray.600', 'secondaryGray.500');

    if (state === 'unreadable') {
        return <ErrorState message={translate(READ_FAILED_I18N_KEY as any)} onRetry={onRetry} />;
    }

    if (state === 'never-issued') {
        return (
            <Flex align='center' justify='center' h='100%' px='20px'>
                <Text color={promptColor} fontSize='sm' fontWeight='500' textAlign='center'>
                    {translate(scopePromptI18nKey(gate) as any)}
                </Text>
            </Flex>
        );
    }

    return null;
}

/**
 * True when the caller should draw its chart rather than defer to the component
 * above.
 *
 * Exported so the three call sites state the condition once and identically —
 * the alternative is three hand-written `state === 'x' || state === 'y'`
 * expressions, and the one that gets a state wrong is wrong in the direction
 * that draws an empty chart, which is the defect.
 */
export function chartHasSomethingToDraw(state: ReadState): boolean {
    return state === 'loading' || state === 'answered';
}
